import React from "react";
import PropTypes from "prop-types";
import NavItem from "../NavItem";
import { filterVisibleItems, isItemActive } from "../utils/itemUtils";

/**
 * ItemSection - A section component for grouping navigation items
 *
 * Renders a group of navigation items.
 * Automatically filters out hidden items and handles item rendering.
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of navigation items to render
 * @param {boolean} props.isExpanded - Whether the navigation is expanded
 * @param {boolean} props.isSmallScreen - Whether currently on a small screen
 * @param {number} [props.expandedItem] - Currently expanded item index
 * @param {Function} props.handleItemClick - Click handler for items
 * @param {Object} props.navItemRefs - Ref object for managing item references
 * @returns {JSX.Element|null} Rendered item section or null if no visible items
 */
const ItemSection = ({
  items,
  isExpanded,
  isSmallScreen,
  expandedItem,
  handleItemClick,
  navItemRefs,
}) => {
  const visibleItems = filterVisibleItems(items);

  if (visibleItems.length === 0) {
    return null;
  }

  const renderItem = (item) => {
    if (item.hide) {
      return null;
    }

    const Component = item.component;
    return (
      <NavItem
        key={item.index}
        item={item}
        isSmallScreen={isSmallScreen}
        Component={Component}
        isActive={isItemActive(item, expandedItem)}
        handleItemClick={handleItemClick}
        navItemRefs={navItemRefs}
        isExpanded={isExpanded}
      />
    );
  };

  return <>{visibleItems.map(renderItem)}</>;
};

ItemSection.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      hide: PropTypes.bool,
      component: PropTypes.elementType,
      actionType: PropTypes.string,
      isActive: PropTypes.bool,
      iconName: PropTypes.string,
      name: PropTypes.string,
      onClick: PropTypes.func,
      bottomAligned: PropTypes.bool,
      pageSpecific: PropTypes.bool,
    })
  ).isRequired,
  isExpanded: PropTypes.bool,
  isSmallScreen: PropTypes.bool,
  expandedItem: PropTypes.number,
  handleItemClick: PropTypes.func.isRequired,
  navItemRefs: PropTypes.object,
};

export default ItemSection;
